import { z } from "zod";
import { BidStatus } from "./car-enums";
import { DebtBreakdownSchema, RELAY_CANCEL_REASONS, RelayRejectReasonSchema, RelayStatusSchema, } from "./relay";
/**
 * Bid contract (`bids` collection).
 *
 * Quirks fixed as types:
 *  - carId = String(car._id) — the Mongo ObjectId AS STRING, NOT listing_id.
 *    (Verified against web api/bids/route.ts; documented in api-node
 *    user.schemas.ts. Confusing these two silently orphans bids.)
 *  - status values are the DISPLAY strings ('Under Review', 'Not Won', …).
 *    Legacy values are normalized by web's normalizeBidStatus() before writes.
 */
export const BidSchema = z
    .object({
    _id: z.unknown().nullish(), // ObjectId | string depending on the reader
    userId: z.string(),
    carId: z.string(), // String(car._id) — NOT listing_id
    bid: z.number(),
    note: z.string().nullish(),
    status: z.nativeEnum(BidStatus),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date().nullish(),
    // Bid history / audit trail
    isActive: z.boolean().nullish(),
    bidVersion: z.number().int().nullish(),
    previousBidId: z.unknown().nullish(),
    bidChainId: z.string().nullish(),
    supersededAt: z.coerce.date().nullish(),
    supersededBy: z.unknown().nullish(),
    // Cancellation
    cancelledReason: z.string().nullish(),
    cancelledAt: z.coerce.date().nullish(),
    // ── Bid-relay desk (Phase 9 CATCH-UP) — ADDITIVE fields only; wire parity
    // for pre-relay documents is preserved (all nullish; absence = 'captured',
    // see effectiveRelayStatus() in relay.ts).
    /** Back-office relay pipeline state (independent of user-facing `status`). */
    relayStatus: RelayStatusSchema.nullish(),
    /** The lot's auction_end_date snapshotted at capture time. */
    tenderCloseAt: z.coerce.date().nullish(),
    /** When an admin confirmed placing the bid on the real auction. */
    relayedAt: z.coerce.date().nullish(),
    /** Admin id who confirmed the relay. ADMIN-ONLY on the wire. */
    relayedBy: z.string().nullish(),
    /** FOUR-EYES-READY second confirmer (recorded, not yet required). ADMIN-ONLY. */
    relaySecondApproverId: z.string().nullish(),
    /** Bid number on the real upstream auction. ADMIN-ONLY on the wire. */
    upstreamRef: z.string().nullish(),
    /** Itemized client debt, set when relayStatus flips to 'won'. */
    debtBreakdown: DebtBreakdownSchema.nullish(),
    /**
     * Why the platform auto-cancelled the relay ('outbid' | 'admin_cancel_all'
     * | 'other_bid_won'). Absent on operator-driven cancellations and on every
     * non-cancelled bid.
     */
    relayCancelReason: z.enum(RELAY_CANCEL_REASONS).nullish(),
    /** Who cancelled (`userId` of the client on a withdraw, admin id otherwise). */
    cancelledBy: z.string().nullish(),
    // ── Phase 13 operator controls — ADDITIVE, all nullish ────────────────
    /** Admin currently working this row (work lock, see RELAY_CLAIM_STALE_MS). ADMIN-ONLY. */
    claimedBy: z.string().nullish(),
    claimedAt: z.coerce.date().nullish(),
    /** Amount actually standing on the real auction (may differ from `bid`). ADMIN-ONLY. */
    upstreamAmount: z.number().nullish(),
    /** When the operator placed it upstream, as they recorded it. ADMIN-ONLY. */
    upstreamPlacedAt: z.coerce.date().nullish(),
    /** Why the upstream auction refused it (relayStatus 'upstream_rejected'). USER-VISIBLE. */
    relayRejectReason: RelayRejectReasonSchema.nullish(),
    /** Operator's free-text note on the rejection. ADMIN-ONLY. */
    relayRejectNote: z.string().nullish(),
    rejectedAt: z.coerce.date().nullish(),
    rejectedBy: z.string().nullish(),
    /** When the CLIENT withdrew the bid (`relayCancelReason: 'client_withdrawn'`). */
    withdrawnAt: z.coerce.date().nullish(),
})
    .passthrough();
/**
 * WHY a client's bid changed status — the additive `reason` on the user-facing
 * `bid-status-change` SSE frame (apps/api realtime.constants BidStatusChangeData).
 *
 * `newStatus` alone cannot tell the client toast what happened: 'Not Won' is
 * written by the client's own withdrawal, by the tender-close expiry of an
 * unrelayed bid, by the relay desk's "upstream rejected", by an admin
 * cancel-all AND by a genuine loss — and each deserves different copy.
 *
 * Optional on the wire: frames produced before this field existed (and any
 * producer that does not set it) fall back to the status-only copy.
 */
export const BID_STATUS_CHANGE_REASONS = [
    /** Another platform bid (or a proxy) overtook this one. */
    "outbid",
    /** The relay desk observed the REAL auction standing at/above this bid. */
    "outbid_upstream",
    /** The admin raised the lot's minimum bid above this bid. */
    "minimum_bid_raised",
    /** The bid won the lot. */
    "won",
    /** The lot was decided against this bid. */
    "lost",
    /** The client withdrew the bid themselves. */
    "withdrawn",
    /** The tender closed before the bid was relayed upstream. */
    "expired",
    /** The operator tried to relay the bid and the upstream auction refused it. */
    "upstream_rejected",
    /** An admin voided every live bid on the lot (cancel-all). */
    "cancelled_by_admin",
    /** A withdrawal above made this bid the lot's leader again. */
    "promoted",
];
export const BidStatusChangeReasonSchema = z.enum(BID_STATUS_CHANGE_REASONS);
//# sourceMappingURL=bid.js.map